import { CaretDownOutlined, CaretUpOutlined, LineChartOutlined } from "@ant-design/icons";
import { EVisionAggregationTypes, EVisionMetricTypes, IMetricsCalculatorConfig } from "@kuika/kuika-cl-model";
import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import KuikaDropdownMenu from "../../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useHistory } from "../history-context";
import ExtendingConnector from "./ExtendingConnector";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

interface MetricsCalculatorNodeData {
  type: "MetricsCalculator";
  title: string;
  config?: IMetricsCalculatorConfig;
  status?: "idle" | "calculating" | "active";
}

interface MetricsCalculatorNodeProps {
  data: MetricsCalculatorNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  analytics: {
    primaryColor: "#f59e0b",
    headerBackground: "rgba(245, 158, 11, 0.1)",
    iconColor: "#f59e0b",
    handleColor: "#f59e0b"
  }
};

const MetricsCalculatorNode = ({ data, id, selected }: MetricsCalculatorNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(data.status || "idle");
  const [config, setConfig] = useState<IMetricsCalculatorConfig>(() => {
    // Default config values matching backend VisionDataConfigModel
    const defaults: IMetricsCalculatorConfig = {
      windowType: EVisionMetricTypes.Hourly,
      windowSize: 1,
      metrics: ["bucketDrops", "averageCycleTime"],
      aggregationType: EVisionAggregationTypes.Count
    };
    // Merge with provided config from template
    if (data.config) {
      return { ...defaults, ...data.config };
    }
    return defaults;
  });
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { createConnectedNode } = useNodeCreation(id);
  const takeSnapshot = useHistory();

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const categoryStyles = NODE_CATEGORY_STYLES.analytics;

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.metricsCalculator.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const updateConfig = (updates: Partial<IMetricsCalculatorConfig>) => {
    // Take snapshot before each config change for undo support
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Take snapshot before deleting
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Sync config state when node data changes from outside (e.g., undo/redo)
  useEffect(() => {
    if (data.config) {
      setConfig((prevConfig) => {
        const stringifiedPrev = JSON.stringify(prevConfig);
        const stringifiedNew = JSON.stringify(data.config);
        // Only update if actually different to avoid unnecessary re-renders
        return stringifiedPrev !== stringifiedNew ? { ...prevConfig, ...data.config } : prevConfig;
      });
    }
  }, [data.config]);

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.windowType} • {config.aggregationType}
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <LineChartOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.metricsCalculator.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Window Type</label>
              <div className={baseStyles.nodrag}>
                <KuikaDropdownMenu
                  options={[
                    { key: EVisionMetricTypes.Hourly, label: "Hourly" },
                    { key: EVisionMetricTypes.Daily, label: "Daily" },
                    { key: EVisionMetricTypes.Weekly, label: "Weekly" },
                    { key: EVisionMetricTypes.Monthly, label: "Monthly" }
                  ]}
                  selected={config.windowType}
                  setSelected={(key) => updateConfig({ windowType: key as EVisionMetricTypes })}
                  size="md"
                >
                  <div className={baseStyles.formSelect} style={{ cursor: "pointer" }}>
                    {config.windowType === EVisionMetricTypes.Hourly && "Hourly"}
                    {config.windowType === EVisionMetricTypes.Daily && "Daily"}
                    {config.windowType === EVisionMetricTypes.Weekly && "Weekly"}
                    {config.windowType === EVisionMetricTypes.Monthly && "Monthly"}
                  </div>
                </KuikaDropdownMenu>
              </div>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Window Size</label>
              <input
                type="number"
                className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                value={config.windowSize}
                onChange={(e) => updateConfig({ windowSize: parseInt(e.target.value) })}
                min="1"
              />
              <small className={baseStyles.hint}>
                {config.windowType === "hourly"
                  ? "hours"
                  : config.windowType === "daily"
                    ? "days"
                    : config.windowType === "weekly"
                      ? "weeks"
                      : "months"}
              </small>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Aggregation Type</label>
              <div className={baseStyles.nodrag}>
                <KuikaDropdownMenu
                  options={[
                    { key: EVisionAggregationTypes.Count, label: "Count" },
                    { key: EVisionAggregationTypes.Average, label: "Average" },
                    { key: EVisionAggregationTypes.Sum, label: "Sum" },
                    { key: EVisionAggregationTypes.Min, label: "Minimum" },
                    { key: EVisionAggregationTypes.Max, label: "Maximum" }
                  ]}
                  selected={config.aggregationType}
                  setSelected={(key) => updateConfig({ aggregationType: key as EVisionAggregationTypes })}
                  size="md"
                >
                  <div className={baseStyles.formSelect} style={{ cursor: "pointer" }}>
                    {config.aggregationType === EVisionAggregationTypes.Count && "Count"}
                    {config.aggregationType === EVisionAggregationTypes.Average && "Average"}
                    {config.aggregationType === EVisionAggregationTypes.Sum && "Sum"}
                    {config.aggregationType === EVisionAggregationTypes.Min && "Minimum"}
                    {config.aggregationType === EVisionAggregationTypes.Max && "Maximum"}
                  </div>
                </KuikaDropdownMenu>
              </div>
            </div>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "active" ? "idle" : "active")}
            >
              {status === "active" ? "Stop" : "Start"} Calculation
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector
          nodeId={id}
          nodeType="metricsCalculator"
          onNodeSelect={handleNodeSelect}
          compact={isCompactMode}
        />
      )}
    </div>
  );
};

export default memo(MetricsCalculatorNode);
